import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import {
  getLoyaltyActivity,
  getLoyaltyActivityDetail,
  postLoyaltyActivity,
  putLoyaltyActivity,
  putLoyaltyActivityType,
  LoyaltyActivityItemType,
  LoyaltyActivityParamsType,
  LoyaltyActivityMutationPayloadType,
} from 'utils/apiList/loyaltyActivity'
import { toastFailed, toastSuccess } from 'utils/toast'
import { RootStateType } from 'store'

interface PaginationStateType {
  pageSize: number
  currentItem: number
  currentPage: number
  totalData: number
  last: boolean
}

interface LoyaltyActivityStateType {
  params: LoyaltyActivityParamsType
  pagination: PaginationStateType
  form: LoyaltyActivityItemType | null
  isLoading: boolean
  isOpenForm: boolean
  isSubmit?: boolean
  data: LoyaltyActivityItemType[]
}

const initialState: LoyaltyActivityStateType = {
  params: {
    name: '',
    sort: 'updated_at',
    direction: 'desc',
    pageSize: 20,
    pageIndex: 0,
  },
  pagination: {
    pageSize: 10,
    currentItem: 0,
    currentPage: 0,
    totalData: 0,
    last: false,
  },
  form: null,
  isLoading: false,
  isOpenForm: false,
  data: [],
}

const loyaltyActivity = createSlice({
  name: 'loyaltyActivity',
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setIsOpenForm: (state, action: PayloadAction<boolean>) => {
      state.isOpenForm = action.payload
      if (!action.payload) state.form = null
    },
    setData: (state, action: PayloadAction<LoyaltyActivityItemType[]>) => {
      state.data = action.payload
    },
    setForm: (state, action: PayloadAction<LoyaltyActivityItemType>) => {
      state.form = action.payload
    },
    setPagination: (state, action: PayloadAction<PaginationStateType>) => {
      state.pagination = action.payload
    },
    setParams: (state, action: PayloadAction<LoyaltyActivityParamsType>) => {
      state.params = action.payload
    },
    reset: (state) => {
      Object.assign(state, { ...initialState })
    },
  },
})

export const { reset, setPagination, setIsOpenForm, setForm, setIsLoading, setData, setParams } =
  loyaltyActivity.actions

export const getData = createAsyncThunk(
  'loyaltyActivity/getData',
  async (_, { dispatch, rejectWithValue, getState }) => {
    try {
      dispatch(setIsLoading(true))
      const {
        loyaltyActivity: { params },
      } = getState() as RootStateType

      const {
        data: {
          data,
          pagination: { pageSize, pageIndex, totalPages, totalElement, numberOfElements },
        },
      } = await getLoyaltyActivity(params)
      dispatch(setData(data))
      dispatch(
        setPagination({
          pageSize,
          currentItem: numberOfElements,
          currentPage: pageIndex,
          totalData: totalElement,
          last: pageIndex === totalPages,
        }),
      )
      return undefined
    } catch (error) {
      toastFailed('Gagal Load Data')
      return rejectWithValue(error)
    } finally {
      dispatch(setIsLoading(false))
    }
  },
)

export const getDetail = createAsyncThunk(
  'loyaltyActivity/getDetail',
  async (id: string | number, { dispatch, rejectWithValue }) => {
    try {
      const {
        data: { data },
      } = await getLoyaltyActivityDetail(id)
      dispatch(setForm(data))
      return undefined
    } catch (error) {
      toastFailed('Gagal Load Data')
      return rejectWithValue(error)
    }
  },
)

export const updateActivity = createAsyncThunk(
  'loyaltyActivity/updateActivity',
  async (
    params: LoyaltyActivityMutationPayloadType & {
      id: string | number
    },
    { dispatch, rejectWithValue },
  ) => {
    try {
      dispatch(setIsLoading(true))
      const { id, ...payload } = params

      const isAllDay = payload?.is_all_day || false

      const startDateMiliSecond = isAllDay
        ? dayjs().startOf('day').valueOf()
        : dayjs(`${payload?.start_date} ${payload?.start_time}`).valueOf()

      const endDateMiliSecond = isAllDay
        ? dayjs().endOf('day').valueOf()
        : dayjs(`${payload?.end_date} ${payload?.end_time}`).valueOf()

      await putLoyaltyActivity(id, {
        ...payload,
        is_all_day: isAllDay,
        start_date: startDateMiliSecond,
        start_time: startDateMiliSecond,
        end_date: endDateMiliSecond,
        end_time: endDateMiliSecond,
        coin_earn: Number(params.coin_earn),
        multiplier: Number(params.multiplier),
      })

      toastSuccess('Successfully update activity')
      dispatch(getData())
      dispatch(setIsOpenForm(false))
      return undefined
    } catch (error) {
      toastFailed('Failed to update activity')
      return rejectWithValue(error)
    } finally {
      dispatch(setIsLoading(false))
    }
  },
)

export const putActivityType = createAsyncThunk(
  'loyaltyActivity/updateActivityType',
  async (
    params: LoyaltyActivityMutationPayloadType & { type: string | number },
    { dispatch, rejectWithValue },
  ) => {
    try {
      dispatch(setIsLoading(true))
      const { type, ...payload } = params
      await putLoyaltyActivityType(type, payload)
      toastSuccess('Successfully update activity')
      dispatch(getData())
      dispatch(setIsOpenForm(false))
    } catch (error) {
      toastFailed('Failed to update activity')
      return rejectWithValue(error)
    } finally {
      dispatch(setIsLoading(false))
    }
    return undefined
  },
)

export const postActivity = createAsyncThunk(
  'loyaltyActivity/postActivity',
  async (params: LoyaltyActivityMutationPayloadType, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setIsLoading(true))

      const isAllDay = params?.is_all_day || false
      const startDateMiliSecond = isAllDay
        ? dayjs().startOf('day').valueOf()
        : dayjs(`${params?.start_date} ${params?.start_time}`).valueOf()

      const endDateMiliSecond = isAllDay
        ? dayjs().endOf('day').valueOf()
        : dayjs(`${params?.end_date} ${params?.end_time}`).valueOf()

      await postLoyaltyActivity({
        ...params,
        is_all_day: isAllDay,
        start_date: startDateMiliSecond,
        start_time: startDateMiliSecond,
        end_date: endDateMiliSecond,
        end_time: endDateMiliSecond,
        coin_earn: Number(params.coin_earn),
        multiplier: Number(params.multiplier),
        is_active: true,
      })

      toastSuccess('Successfully create activity')
      dispatch(getData())
      dispatch(setIsOpenForm(false))
    } catch (error) {
      toastFailed('Failed to create activity')
      return rejectWithValue(error)
    } finally {
      dispatch(setIsLoading(false))
    }
    return undefined
  },
)

export default loyaltyActivity.reducer
